import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Header from '../../main/components/Header';
import MainImageSlider from '../../announcement/components/MainImageSlider';
import Description from '../components/Description';
import Path from '../../path/ui/Path';
import MobileBottomBar from '../../main/components/MobileBottomBar';
import { postAnnouncement } from '../../../api/createannouncement';
import { Buttons } from '../components/Buttons';
import { useTranslation } from 'react-i18next';

import { clearDB } from '../../../utils/indexedDBService';
import monitoringService from '../../../services/monitoringService';

const Review = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const previewData = location.state?.previewData;

    const [isLoading, setIsLoading] = useState(false);

    if (!previewData) {
        return (
            <div className="error_message">
                <p>No data to preview. Please fill out the form first.</p>
                <button onClick={() => navigate(-1)}>Go Back</button>
            </div>
        );
    }

    const handlePublish = async () => {
        setIsLoading(true);

        const formData = new FormData();

        formData.append("title", previewData.title || "");
        formData.append("video_url", previewData.videoLink || "");
        formData.append("description", previewData.description || "");
        formData.append("price_type", previewData.priceType || "");
        formData.append("price", previewData.priceType === "fixed" ? previewData.price : "");
        formData.append("isBarter", previewData.isBarter ? "true" : "false");

        if (previewData.selectedCategory) {
            formData.append("category_id", previewData.selectedCategory);
        } else {
            alert("Выберите категорию!");
            setIsLoading(false);
            return;
        }

        if (previewData.selectedSubcategory) {
            formData.append("subcategory_id", previewData.selectedSubcategory);
        } else {
            alert("Выберите подкатегорию!");
            setIsLoading(false);
            return;
        }

        if (previewData.selectedLocation) {
            const locId = previewData.selectedLocation.user_location_id ?? previewData.selectedLocation.address_id;
            if (locId != null && locId !== "") {
                formData.append("user_location_id", String(locId));
            } else {
                if (previewData.selectedLocation.country_id != null) {
                    formData.append("country_id", String(previewData.selectedLocation.country_id));
                }
                if (previewData.selectedLocation.region_id != null) {
                    formData.append("region_id", String(previewData.selectedLocation.region_id));
                }
                if (previewData.selectedLocation.district_id != null) {
                    formData.append("district_id", String(previewData.selectedLocation.district_id));
                }
            }
        } else {
            alert("Выберите местоположение!");
            setIsLoading(false);
            return;
        }

        if (previewData.volumeUnit) {
            const volumeUnitMap = {
                "1 Штук": "piece",
                "1 Килограм": "kilogram",
                "1 Тонна": "ton",
                "1 Литр": "liter",
                "1 Гектар": "hectare",
                "1 Шаршы метр": "square_meter",
                "1 Куб метр": "cubic_meter",
                "1 Кап": "bag",
                "1 Центнер": "centner",
                "1 Бас": "head",
                "1 Пара": "pair",
                "1 Метр": "meter",
                "1 Коробка": "box",
                // Ensure mapping for values coming from backend too
                "piece": "piece",
                "kilogram": "kilogram",
                "ton": "ton",
                "liter": "liter",
                "hectare": "hectare",
                "square_meter": "square_meter",
                "cubic_meter": "cubic_meter",
                "bag": "bag",
                "centner": "centner",
                "head": "head",
                "pair": "pair",
                "meter": "meter",
                "box": "box"
            };

            const convertedVolumeUnit = volumeUnitMap[previewData.volumeUnit] || previewData.volumeUnit;
            formData.append("measurement_unit", convertedVolumeUnit);
        } else {
            alert("Выберите {t('units')}у измерения!");
            setIsLoading(false);
            return;
        }

        if (Array.isArray(previewData.additionalPhones)) {
            previewData.additionalPhones.forEach((phone, index) => {
                formData.append(`additionalPhones[${index}]`, phone);
            });
        }

        if (Array.isArray(previewData.tagsArray)) {
            previewData.tagsArray.forEach(tag => {
                formData.append("tags[]", tag.trim());
            });
        }


        if (Array.isArray(previewData.imageFiles) && previewData.imageFiles.length > 0) {
            previewData.imageFiles.forEach(file => formData.append("images", file));
        } else if (Array.isArray(previewData.images)) {
            // Fallback: keep old behavior for compatibility, but ignore fetch errors
            try {
                const imageFiles = await Promise.all(
                    previewData.images.map(async (file) => {
                        if (file instanceof File) return file;
                        if (typeof file === "string") {
                            const response = await fetch(file);
                            const blob = await response.blob();
                            return new File([blob], "image.jpg", { type: blob.type });
                        }
                        return null;
                    })
                );
                imageFiles
                    .filter(Boolean)
                    .forEach(file => formData.append("images", file));
            } catch (e) {
                console.warn("Failed to restore images from URLs, continuing without them:", e);
            }
        }

        try {
            const accessToken = localStorage.getItem("access_token");
            const response = await postAnnouncement(formData, accessToken);

            if (response?.announcement_id) {
                localStorage.removeItem("priceType");
                localStorage.removeItem("announcementName");
                localStorage.removeItem("videoLink");
                localStorage.removeItem("description");
                localStorage.removeItem("price");
                localStorage.removeItem("tagsArray");
                localStorage.removeItem("volumeUnit");
                localStorage.removeItem("selectedLocation");
                localStorage.removeItem("selectedCategory");
                localStorage.removeItem("selectedCategoryName");
                localStorage.removeItem("selectedSubcategory");
                localStorage.removeItem("selectedSubcategoryName");
                clearDB("savedImages")
                clearDB("savedImageFiles")
                
                // Track successful advertisement creation
                monitoringService.trackAdvertise(
                    response.announcement_id,
                    true,
                    null,
                    {
                        categoryId: previewData.selectedCategory,
                        subcategoryId: previewData.selectedSubcategory,
                        priceType: previewData.priceType,
                        price: previewData.price,
                        isBarter: previewData.isBarter,
                    }
                ).catch(err => {
                    console.warn('Failed to track advertisement:', err);
                });
                
                navigate(`/announcement/${response.announcement_id}`);
            } else {
                const errorMsg = "Announcement created but ID is missing in the response";
                console.error(errorMsg);
                // Track failed advertisement creation
                monitoringService.trackAdvertise(null, false, errorMsg).catch(err => {
                    console.warn('Failed to track advertisement failure:', err);
                });
            }
        } catch (error) {
            const errorMsg = error.message || "Error creating announcement";
            console.error(errorMsg, error);
            // Track failed advertisement creation
            monitoringService.trackAdvertise(null, false, errorMsg).catch(err => {
                console.warn('Failed to track advertisement failure:', err);
            });
        } finally {
            setIsLoading(false);
        }
    };

    const { title, description, price, priceType, images, videoLink, user_name, member_since, user_rating, user_location, volumeUnit, isBarter } = previewData;

    const user = {
        name: user_name,
        member_since: member_since,
        rating: user_rating,
    };

    return (
        <div className="min-h-screen flex flex-col bg-[var(--background-color,#F5F5F5)]">
            <Header />
            <Path currentPath={t('previewTitle')} />
            <div className="w-full flex flex-col px-5 md:px-[60px] lg:px-[120px] xl:px-[240px] pb-[100px]">
                <div className="flex flex-col md:flex-row md:items-start gap-6 mt-5">
                    <MainImageSlider
                        id={"55774869"}
                        images={Array.isArray(images) && images.length > 0 ? images : []}
                        description={description || "No description available"}
                        views={0}
                        videoUrl={videoLink || null}
                        isLoading={isLoading}
                    />
                    <Buttons
                        handlePublish={handlePublish}
                        isLoading={isLoading}
                        isMobile={false}
                    />
                    <div className="flex flex-col md:w-[30%] md:ml-5 mt-5 md:mt-0">
                        <Description
                            title={title || "Untitled"}
                            price={`${price || 0} тг. `}
                            user={user}
                            location={user_location}
                            unit={volumeUnit}
                            isBarter={isBarter}
                            priceType={priceType}
                        />
                    </div>
                </div>
                <MobileBottomBar />
                <Buttons
                    handlePublish={handlePublish}
                    isLoading={isLoading}
                    isMobile={true}
                />
            </div>
        </div>
    );
};

export default Review;